import { computed, effect, inject, Injectable, signal, Signal } from "@angular/core";
import { catchError, filter, map, Observable, of, tap, throwError } from "rxjs";

import { BaseService } from "@mp/common/base/base.service";
import { BaseEntity } from "@mp/common/entities/base.entity";
import { ObjectUtil } from "@mp/common/helpers/object-util";
import { StorageService } from "@mp/core/services/storage.service";
import { MpSdkService } from "@m481/mp-sdk-ng";
import { BaseModel as SdkBaseModel } from "@m481/mp-sdk-ng/lib/models/base.model";
import { IListDataResponse } from "@m481/mp-sdk-ng/lib/models/reponses/list-data.response";

export enum BACKEND_ERROR_CODE {
    VALIDATION_NOT_UNIQUE = "validation_not_unique"
}

export interface PersistenceConfig {
    storageKey: string;
    debounceTime?: number;
}

export interface ChangeSet<T> {
    added: Array<T>;
    updated: Array<T>;
    removed: Array<T>;
}

@Injectable({
    providedIn: null
})
export abstract class BaseStoreService<E extends BaseEntity, M extends SdkBaseModel> extends BaseService {
    protected readonly entities: Signal<Array<E>>;
    protected readonly changeSet: Signal<ChangeSet<E>>;
    protected readonly initialSyncPending: Signal<boolean>;

    protected readonly storageService = inject(StorageService);
    protected readonly mpSdkService = inject(MpSdkService);

    private readonly _entities = signal<Array<E>>([]);
    private readonly _changeSet = signal<ChangeSet<E>>({
        added: [],
        updated: [],
        removed: []
    });

    private readonly _initialSyncPending = signal<boolean>(true);
    private readonly persistenceConfig?: PersistenceConfig;
    private autoRefreshTimerId?: number;
    private _prevMap = new Map<string, E>();

    private readonly REFRESH_INTERVAL = 10 * 2 * 30 * 1000; // 10 minutos

    constructor(config?: PersistenceConfig) {
        super();

        this.persistenceConfig = {
            debounceTime: this.REFRESH_INTERVAL,
            ...config
        };

        this.entities = this._entities.asReadonly();
        this.changeSet = this._changeSet.asReadonly();
        this.initialSyncPending = this._initialSyncPending.asReadonly();

        effect(() => {
            const currentEntities = this._entities();
            this.storeComputeAndPublishChangeSet(currentEntities);
        });
    }

    public init(): void {
        this.localStorageHydrateStoreEntities();
        this.startAutoRefresh();
    }

    public reset(): void {
        this.stopAutoRefresh();
        this._entities.set([]);
        this._changeSet.set({ added: [], updated: [], removed: [] });
        this._prevMap = new Map<string, E>();
        this._initialSyncPending.set(true);
        if (this.persistenceConfig?.storageKey) {
            this.storageService.removeItem(this.persistenceConfig.storageKey);
        }
        this.log.debug("store reset", { key: this.persistenceConfig?.storageKey });
    }

    /*
     * Selectors
     */

    public selectInitialSyncPending(): Signal<boolean> {
        return this.initialSyncPending;
    }

    public selectAll(): Signal<Array<E>> {
        return this.entities;
    }

    public selectById(id: string): Signal<E | undefined> {
        return computed(() => this.entities().find(e => e.id === id));
    }

    public selectWhere(predicate: (e: E) => boolean): Signal<Array<E>> {
        return computed(() => this.entities().filter(predicate));
    }

    public selectChangeSet(): Signal<ChangeSet<E>> {
        return this.changeSet;
    }

    /*
     * Other basic operations
     */

    public create$(entity: Partial<E>): Observable<E> {
        return this.sdkCreateEntity$(entity);
    }

    public update$(entity: E): Observable<E> {
        return this.sdkUpdateEntity$(entity);
    }

    public delete$(id: string): Observable<boolean> {
        return this.sdkDeleteEntity$(id);
    }

    public refresh(): Observable<Array<E>> {
        return this.sdkReadEntities$();
    }

    /*
     * Drafts
     */

    public persistEntityDraft(entity: Partial<E>): void {
        this.storeCreateEntity({
            ...entity,
            id: `DRAFT-${Date.now().toString()}`,
            isDraft: true
        } as E);
        this.localStoragePersistStoreEntities();
    }

    public removeEntityDraft(id?: string): void {
        const localStorageEntities = this.storeReadEntities() as Array<E>;
        let entityDraft: E | undefined;
        if (id) {
            entityDraft = localStorageEntities.find(e => e.id === id && e.isDraft);
        } else {
            entityDraft = localStorageEntities.find(e => e.isDraft);
        }

        if (entityDraft) {
            this.storeDeleteEntity(entityDraft.id);
            this.localStoragePersistStoreEntities();
        }
    }

    /*
     * SDK methods, implemented in entity subclasses
     */

    protected abstract mapEntityToModel(entity: Partial<E>): M;

    protected abstract mapModelToEntity(model: M): E;

    protected abstract sdkGet$(): Observable<IListDataResponse<M>>;

    protected abstract sdkCreate$(model: M): Observable<M>;

    protected abstract sdkUpdate$(model: M): Observable<M>;

    protected abstract sdkDelete$(id: string): Observable<boolean>;

    /*
     * Methods to manage CRUD operations in the store, the local storage and the backend
     */

    protected storeCreateEntity(entity: E): void {
        this._entities.update(entities => [...entities, entity]);
        this.log.debug("created entity in store", entity);
    }

    protected storeReadEntities(id?: string): Array<E> | E | undefined {
        const entities = this._entities();
        if (!id) {
            return entities;
        }

        const entity = entities.find(entity => entity.id === id);
        if (!entity) {
            this.log.debug("read entity not found in store", id);
        }
        return entity;
    }

    protected storeUpdateEntity(id: string, entity: Partial<E>): void {
        this._entities.update(entities =>
            entities.map(e => {
                if (e.id !== id) {
                    return e;
                }
                return { ...e, ...entity };
            })
        );
        this.log.debug("updated entity in store", { id, entity });
    }

    protected storeDeleteEntity(id: string): void {
        this._entities.update(entities => entities.filter(item => item.id !== id));
        this.log.debug("deleted entity in store", id);
    }

    protected storeSetAllEntities(entities: Array<E>): void {
        this._entities.set(entities);
        this.log.debug("setting all entities in store", { count: entities.length });
    }

    protected storeComputeAndPublishChangeSet(current: Array<E>): void {
        const currMap = new Map<string, E>();
        current.forEach(e => currMap.set(e.id, e));

        const added: Array<E> = [];
        const updated: Array<E> = [];
        const removed: Array<E> = [];

        // Detect added and updated
        for (const [id, curr] of currMap.entries()) {
            const prev = this._prevMap.get(id);
            if (!prev) {
                added.push(curr);
            } else if (!ObjectUtil.shallowEqual(prev, curr)) {
                updated.push(curr);
            }
        }

        // Detect removed
        for (const [id, prev] of this._prevMap.entries()) {
            if (!currMap.has(id)) {
                removed.push(prev);
            }
        }

        if (added.length || updated.length || removed.length) {
            this._changeSet.set({ added, updated, removed });
            this._prevMap = currMap;
        }

        this.log.debug("change set computed", { added, updated, removed });
    }

    protected localStorageHydrateStoreEntities(): void {
        if (!this.persistenceConfig?.storageKey) {
            return;
        }

        try {
            const storedData = this.storageService.getObject<Array<E>>(this.persistenceConfig.storageKey) ?? [];
            this.log.debug("hydrating from storage", {
                key: this.persistenceConfig.storageKey,
                count: storedData.length
            });
            this.storeSetAllEntities(storedData);
        } catch (error) {
            this.log.err("error hydrating from storage", error);
        }
    }

    protected localStoragePersistStoreEntities(): void {
        if (!this.persistenceConfig?.storageKey) {
            return;
        }

        const entities = this.storeReadEntities();

        try {
            this.storageService.setObject(this.persistenceConfig.storageKey, entities);
            this.log.debug("persisted entities", {
                key: this.persistenceConfig.storageKey,
                count: Array.isArray(entities) ? entities.length : 1
            });
        } catch (error) {
            this.log.err("error persisting entities", error);
        }
    }

    protected sdkCreateEntity$(entity: Partial<E>): Observable<E> {
        const model = this.mapEntityToModel(entity);
        return this.sdkCreate$(model).pipe(
            tap(model => {
                this.log.debug("created entity in backend", model);
                this.removeEntityDraft();
                this.storeCreateEntity(this.mapModelToEntity(model));
                this.localStoragePersistStoreEntities();
            }),
            map(model => this.mapModelToEntity(model)),
            catchError(error => {
                this.log.err("error creating entity in backend", error);
                this.removeEntityDraft();
                this.localStorageHydrateStoreEntities();
                return throwError(() => error);
            })
        );
    }

    protected sdkReadEntities$(): Observable<Array<E>> {
        //TODO: manage pagination
        return this.sdkGet$().pipe(
            tap((response: IListDataResponse<M>) => {
                this.log.debug("read entities in backend", response);
                const items = Array.isArray(response.items) ? response.items : [];
                this.storeSetAllEntities(items.map(model => this.mapModelToEntity(model)));
                this.localStoragePersistStoreEntities();

                if (this._initialSyncPending()) {
                    this._initialSyncPending.set(false);
                }
            }),
            map((response: IListDataResponse<M>) => {
                const items = Array.isArray(response.items) ? response.items : [];
                return items.map(model => this.mapModelToEntity(model));
            }),
            catchError(error => {
                this.log.err("error reading entities in backend", error);
                this.localStorageHydrateStoreEntities();

                if (this._initialSyncPending()) {
                    this._initialSyncPending.set(false);
                }
                return of<Array<E>>([]);
            })
        );
    }

    protected sdkUpdateEntity$(entity: E): Observable<E> {
        return this.sdkUpdate$(this.mapEntityToModel(entity)).pipe(
            tap(model => {
                this.log.debug("updated entity in backend", model);
                this.storeUpdateEntity(entity.id, this.mapModelToEntity(model));
                this.localStoragePersistStoreEntities();
            }),
            map(model => this.mapModelToEntity(model)),
            catchError(error => {
                this.log.err("error updating entity in backend", error);
                this.localStorageHydrateStoreEntities();
                return throwError(() => error);
            })
        );
    }

    protected sdkDeleteEntity$(id: string): Observable<boolean> {
        return this.sdkDelete$(id).pipe(
            filter(deleted => deleted),
            tap(() => {
                this.log.debug("deleted entity in backend", id);
                this.storeDeleteEntity(id);
                this.localStoragePersistStoreEntities();
            }),
            catchError(error => {
                this.log.err("error deleting entity in backend", error);
                this.localStorageHydrateStoreEntities();
                return throwError(() => error);
            })
        );
    }

    protected startAutoRefresh(): void {
        this.sdkReadEntities$().subscribe();

        this.stopAutoRefresh();
        this.log.debug("starting auto refresh", { intervalMs: this.persistenceConfig.debounceTime || this.REFRESH_INTERVAL });

        this.autoRefreshTimerId = window.setInterval(async () => {
            try {
                this.sdkReadEntities$().subscribe();
            } catch (error) {
                this.log.err("auto refresh error", error);
            }
        }, this.persistenceConfig.debounceTime || this.REFRESH_INTERVAL);
    }

    protected stopAutoRefresh(): void {
        if (this.autoRefreshTimerId) {
            clearInterval(this.autoRefreshTimerId);
            this.autoRefreshTimerId = undefined;
            this.log.debug("stopped auto refresh");
        }
    }
}
